import { applyUpdate, encodeStateAsUpdate } from "yjs"
import type { Doc } from "yjs"

/**
 * Browser-safe Y.js snapshot helpers + a small REST client for the unified
 * version-history API (TDD-0026 Phase 5). Lives here, not in `@workspace/ui`,
 * so the UI package stays yjs-free — this package already owns the Y.js
 * provider and depends on `yjs`.
 *
 * Wire contract (uniform across docs / canvas / sheets / variant-content):
 *
 *   GET  {versionsEndpoint}            → VersionMeta[]   (blob excluded)
 *   POST {versionsEndpoint}            → { id }          body: { snapshot, label?, nodeCount? }
 *   GET  {versionsEndpoint}/{id}       → VersionMeta & { snapshot: base64 }
 *
 * Restore is client-side merge: fetch the single version's snapshot blob and
 * `applyUpdate()` it into the live local doc. The merge propagates to peers via
 * normal Y.js sync (no server-side restore endpoint in this phase).
 */

export type SnapshotTriggerLabel =
  | "manual"
  | "auto"
  | "auto-idle"
  | "auto-force"
  | "auto-disconnect"
  | (string & {})

export interface VersionMeta {
  id: string
  label: string | null
  authorId: string | null
  trigger: SnapshotTriggerLabel
  /** App-specific size metric (nodes / rows / blocks); null when not tracked. */
  nodeCount: number | null
  /** ms epoch or ISO string — the UI normalises with `new Date(...)`. */
  createdAt: number | string
}

/** Encode the current Y.Doc state as a base64 string (browser-safe). */
export function captureSnapshot(ydoc: Doc): string {
  const bytes = encodeStateAsUpdate(ydoc)
  let binary = ""
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!)
  }
  return btoa(binary)
}

/**
 * Apply a base64 snapshot into a live Y.Doc. Uses `applyUpdate`, so the
 * snapshot state is *merged* (CRDT union), never a destructive replace.
 */
export function applySnapshot(ydoc: Doc, snapshotBase64: string): void {
  const binary = atob(snapshotBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  applyUpdate(ydoc, bytes)
}

async function jsonOrThrow<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    throw new Error(`${what} failed: ${res.status}`)
  }
  return res.json() as Promise<T>
}

/**
 * REST client bound to a single document's version endpoints. `versionsEndpoint`
 * is the collection URL (list + create); `{versionsEndpoint}/{id}` is the item
 * URL (fetch-with-blob). All requests send credentials for cookie auth.
 */
export function createVersionsClient(versionsEndpoint: string) {
  const item = (id: string) => `${versionsEndpoint.replace(/\/$/, "")}/${id}`

  return {
    /** List versions (no snapshot blobs). */
    async list(): Promise<VersionMeta[]> {
      const res = await fetch(versionsEndpoint, { credentials: "include" })
      const data = await jsonOrThrow<VersionMeta[] | { versions: VersionMeta[] }>(
        res,
        "List versions",
      )
      // Tolerate both bare-array and { versions } envelope responses.
      return Array.isArray(data) ? data : data.versions
    },

    /** Save a manual snapshot from the current doc state. Returns the new id. */
    async save(
      ydoc: Doc,
      opts: { label?: string; nodeCount?: number } = {},
    ): Promise<{ id: string }> {
      const res = await fetch(versionsEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          snapshot: captureSnapshot(ydoc),
          label: opts.label,
          nodeCount: opts.nodeCount,
          trigger: "manual",
        }),
      })
      return jsonOrThrow<{ id: string }>(res, "Save version")
    },

    /** Fetch a single version including its base64 snapshot blob. */
    async fetchOne(id: string): Promise<VersionMeta & { snapshot: string }> {
      const res = await fetch(item(id), { credentials: "include" })
      return jsonOrThrow<VersionMeta & { snapshot: string }>(res, "Fetch version")
    },

    /**
     * Restore a version into the live doc via client-side merge. Fetches the
     * snapshot blob and applies it; the doc's provider syncs the merge to peers.
     */
    async restore(ydoc: Doc, id: string): Promise<VersionMeta> {
      const version = await this.fetchOne(id)
      applySnapshot(ydoc, version.snapshot)
      const { snapshot: _omit, ...meta } = version
      return meta
    },
  }
}

export type VersionsClient = ReturnType<typeof createVersionsClient>
